import { message, Row, Col, Space, Table, Button, Empty, Card } from "antd";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setAnalyticsFilterOptions, setLinesProductVariants } from "../../redux/analytics/actions";
import { getDurationFromSecond } from "../../services/utils/analyticsUtils";
import ColorIndicator from "./ColorIndicator";
import SummaryCard from "./SummaryCard";
import SummaryGrid from "./SummaryGrid";
import { ReloadOutlined, InfoCircleOutlined } from "@ant-design/icons";
import CustomTooltip from "../Components/Modals/CustomTooltip";
import { variantTokens, primaryShades } from "../../theme/theme";
import { formatNumberBritish } from "../../utils/numberFormatting";
import LoadingCard from "../Components/Cards/LoadingCard";

function AnalyticsTable({ aggregateList = [], loading }) {
    const [productsList, setProductsList] = useState([]);
    const [variantsList, setVariantsList] = useState([]);
    const [selectedTab, setSelectedTab] = useState("Products");
    const [dataInitialized, setDataInitialized] = useState(false);

    // Memorizza i dati originali completi
    const productsRef = useRef([]);
    const variantsRef = useRef([]);

    const dispatch = useDispatch();
    const filterOptions = useSelector((state) => state.analytics.filterOptions);

    const { glass, variant } = filterOptions;
    const [inloading, setLoader] = useState(loading);

    const filterOptionMap = {
        Products: "glass",
        Variants: "variant",
    };

    const tabMap = useMemo(() => {
        return {
            Products: productsList,
            Variants: variantsList,
        };
    }, [productsList, variantsList]);

    const tooltipTextMap = {
        Products: "The sum of time that the users spent trying-on a product",
        Variants: "The sum of time that the users spent trying-on a variant",
    };

    const [list, totalRow] = useMemo(() => {
        const tempList = tabMap[selectedTab];
        const totalRow = tempList.find(
            (row) => row?.id === "total"
        );
        const restList = tempList.filter(
            (row) => row?.id !== "total"
        );
        return [restList, totalRow];
    }, [tabMap, selectedTab]);

    const handleDataIndexChange = (key) => {
        setSelectedTab(key);
    };

    const handleSelectionChange = (optionName, value) => (e) => {
        if (value === 'total') return;

        // Ferma la propagazione dell'evento se presente
        if (e && e.stopPropagation) {
            e.stopPropagation();
            e.preventDefault();
        }

        try {
            // Assicurati che value sia una stringa
            const safeValue = typeof value === 'string' ? value :
                (value && value.id ? value.id : String(value));

            // Determina il valore di glass in base al tipo di selezione
            let glassValue = filterOptions.glass;
            if (optionName === 'glass') {
                glassValue = safeValue;
            } else if (optionName === 'variant') {
                // Trova il productRef per la variante selezionata
                const variant = variantsRef.current.find(v => v.id === safeValue);
                if (variant) {
                    // Assicurati che productRef sia una stringa
                    glassValue = typeof variant.productRef === 'string' ? variant.productRef :
                        (variant.productRef && variant.productRef.id ? variant.productRef.id :
                            String(variant.productRef || ''));
                }
            }

            // Crea un nuovo oggetto per assicurarsi che il redux riconosca il cambiamento
            const updatedFilterOptions = {
                ...filterOptions,
                glass: glassValue,
                variant: optionName === 'variant' ? safeValue : ''
            };

            // Esegui immediatamente il dispatch per aggiornare lo stato
            dispatch(setAnalyticsFilterOptions(updatedFilterOptions));
        } catch (error) {
            message.error("Error in selection. Please try again.");
        }
    };

    // Elabora i dati da aggregateList
    const processAggregateData = () => {
        setLoader(true);

        try {
            if (aggregateList.length > 0) {
                let productsMap = new Map();
                let variantsMap = new Map();
                let productToVariantsMap = new Map(); // Mappa prodotto -> varianti

                // Inizializza i totali
                const productsTotal = { id: 'total', name: 'Total', duration: 0, visualization: 0, sessions: 0 };
                const variantsTotal = { id: 'total', name: 'Total', duration: 0, visualization: 0, sessions: 0 };

                // Elabora i dati da aggregateList
                aggregateList.forEach(aggregate => {
                    if (!aggregate) return;

                    const { list_products = [], list_variants = [] } = aggregate;

                    // Elabora prodotti
                    list_products.forEach(product => {
                        if (!product) return;

                        const productId = product.productRef || product.glassesRef;
                        if (!productId) {
                            return;
                        }

                        // Aggiorna totali - stesso valore per entrambi i totali
                        const durationValue = product.time || 0;
                        const visualizationValue = product.visualization || 0;
                        const sessionsValue = product.numberOfSessions || 1;

                        productsTotal.duration += durationValue;
                        productsTotal.visualization += visualizationValue;
                        productsTotal.sessions += sessionsValue;

                        // Copiamo gli stessi valori anche nei totali delle varianti
                        variantsTotal.duration += durationValue;
                        variantsTotal.visualization += visualizationValue;
                        variantsTotal.sessions += sessionsValue;

                        // Aggiorna o crea dato del prodotto
                        if (productsMap.has(productId)) {
                            const existingProduct = productsMap.get(productId);
                            existingProduct.duration += durationValue;
                            existingProduct.visualization += visualizationValue;
                            existingProduct.sessions += sessionsValue;
                        } else {
                            productsMap.set(productId, {
                                id: productId,
                                name: product.productName || product.glassesName || product.nome_modello || productId,
                                duration: durationValue,
                                visualization: visualizationValue,
                                sessions: sessionsValue
                            });
                        }
                    });

                    // Elabora varianti - SENZA aggiornare i totali (già aggiornati dai prodotti)
                    list_variants.forEach(variant => {
                        if (!variant) return;

                        const variantId = variant.variantRef || variant.modelRef;
                        if (!variantId) {
                            return;
                        }

                        const productId = variant.productRef || variant.glassesRef;
                        if (!productId) {
                            return;
                        }

                        // Traccia la relazione prodotto -> variante
                        if (!productToVariantsMap.has(productId)) {
                            productToVariantsMap.set(productId, new Set());
                        }
                        productToVariantsMap.get(productId).add(variantId);

                        // NON aggiorniamo più i totali delle varianti qui
                        // Useranno gli stessi totali dei prodotti

                        // Aggiorna o crea dato della variante
                        if (variantsMap.has(variantId)) {
                            const existingVariant = variantsMap.get(variantId);
                            existingVariant.duration += variant.time || 0;
                            existingVariant.visualization += variant.visualization || 0;
                            existingVariant.sessions += variant.numberOfSessions || 1;
                        } else {
                            // Controlla se ci sono colori da utilizzare
                            let name = variant.variantName || variant.nomeOcchiale || variant.nome || variantId;

                            // Gestione colori per le varianti (se i dati sono disponibili)
                            const primaryFrameColor =
                                variant.primaryFrameColorHex ||
                                variant.esaColorFramePrimary ||
                                variant.colorFramePrimary;

                            const secondaryFrameColor =
                                variant.secondaryFrameColorHex ||
                                variant.esaColorFrameSecondary ||
                                variant.colorFrameSecondary;

                            const primaryLensesColor =
                                variant.primaryLensesColorHex ||
                                variant.esaColorLensesPrimary ||
                                variant.colorLensesPrimary;

                            const secondaryLensesColor =
                                variant.secondaryLensesColorHex ||
                                variant.esaColorLensesSecondary ||
                                variant.colorLensesSecondary;

                            // Crea componente React con nome e indicatori di colore
                            if (primaryFrameColor || primaryLensesColor) {
                                name = (
                                    <Space size="small" align="center">
                                        <span>{name}</span>
                                        {primaryFrameColor && (
                                            <ColorIndicator
                                                primary={primaryFrameColor}
                                                secondary={secondaryFrameColor}
                                                type="square"
                                                size="small"
                                                title="Frame color"
                                            />
                                        )}
                                        {primaryLensesColor && (
                                            <ColorIndicator
                                                primary={primaryLensesColor}
                                                secondary={secondaryLensesColor}
                                                size="small"
                                                title="Lens color"
                                            />
                                        )}
                                    </Space>
                                );
                            }

                            // Aggiungi la variante alla mappa
                            variantsMap.set(variantId, {
                                id: variantId,
                                name: name,
                                duration: variant.time || 0,
                                visualization: variant.visualization || 0,
                                sessions: variant.numberOfSessions || 1,
                                productRef: productId,
                                sku: variant.sku || variant.skuModel || variant.modelSku || '',
                                frameColor: variant.frameColor || variant.colorFrame || variant.primaryFrameColor || ''
                            });
                        }
                    });
                });

                // Converte le mappe in array
                const allProducts = Array.from(productsMap.values());
                const allVariants = Array.from(variantsMap.values());

                // Salva i dati completi nei riferimenti
                productsRef.current = [productsTotal, ...allProducts];
                variantsRef.current = [variantsTotal, ...allVariants];

                // Imposta il flag di inizializzazione dati
                setDataInitialized(true);

                // IMPORTANTE: Inizializza le liste utilizzando i dati originali
                // quando non ci sono filtri attivi
                if (!glass && !variant) {
                    setProductsList([productsTotal, ...allProducts]);
                    setVariantsList([variantsTotal, ...allVariants]);
                }
            } else {
                // Resetta tutto se non ci sono dati
                productsRef.current = [];
                variantsRef.current = [];
                setProductsList([]);
                setVariantsList([]);
                setDataInitialized(false);
            }
        } catch (error) {
            // Silent error
        } finally {
            setLoader(false);
        }
    };

    // Elabora i dati quando aggregateList cambia
    useEffect(() => {
        processAggregateData();
    }, [aggregateList]);

    // Aggiorna lo stato Redux con i prodotti disponibili per popolare la select in ChartFilter
    useEffect(() => {
        // Controlla se ci sono prodotti da inviare a Redux
        if (productsRef.current.length > 1) { // Verifichiamo che ci siano prodotti (escludiamo solo il totale)
            const products = productsRef.current
                .filter(p => p.id !== 'total')
                .map(p => ({
                    id: p.id,
                    productName: typeof p.name === 'string' ? p.name : 'Product',
                    productRef: p.id
                }));

            if (products.length > 0) {
                // Invia i prodotti al Redux store
                dispatch(setLinesProductVariants({
                    list_products: products
                }));
            }
        }
    }, [dispatch, aggregateList, productsRef?.current?.length, dataInitialized]);

    // Applica i filtri quando cambiano o i dati vengono inizializzati
    useEffect(() => {
        // Se i dati non sono ancora inizializzati, esci
        if (!dataInitialized) {
            return;
        }

        setLoader(true);

        try {
            // Caso: nessun filtro attivo
            if (!glass && !variant) {
                // Utilizza i dati completi
                setProductsList([...productsRef.current]);
                setVariantsList([...variantsRef.current]);
            }
            // Caso: filtro per prodotto attivo
            else if (glass && !variant) {
                // Trova il prodotto selezionato
                const selectedProduct = productsRef.current.find(p => p.id !== 'total' && p.id === glass);
                if (!selectedProduct) {
                    // Se il prodotto non è trovato, usa i dati completi
                    setProductsList([...productsRef.current]);
                    setVariantsList([...variantsRef.current]);
                    setLoader(false);
                    return;
                }

                // Crea un totale specifico per il prodotto filtrato
                const productTotal = {
                    id: 'total',
                    name: 'Total',
                    duration: selectedProduct.duration || 0,
                    visualization: selectedProduct.visualization || 0,
                    sessions: selectedProduct.sessions || 0
                };

                // Filtra le varianti associate al prodotto
                const filteredVariants = variantsRef.current.filter(v =>
                    v.id !== 'total' &&
                    String(v.productRef) === String(glass)
                );

                // Calcola il totale per le varianti filtrate
                const variantTotal = {
                    id: 'total',
                    name: 'Total',
                    duration: 0,
                    visualization: 0,
                    sessions: 0
                };

                filteredVariants.forEach(v => {
                    variantTotal.duration += v.duration || 0;
                    variantTotal.visualization += v.visualization || 0;
                    variantTotal.sessions += v.sessions || 0;
                });

                // Aggiorna le liste visualizzate
                setProductsList([productTotal, selectedProduct]);
                setVariantsList([variantTotal, ...filteredVariants]);
            }
            // Caso: filtro per variante attivo
            else if (variant) {
                // Trova la variante selezionata
                const selectedVariant = variantsRef.current.find(v => v.id !== 'total' && v.id === variant);
                if (!selectedVariant) {
                    // Se la variante non è trovata, usa i dati completi
                    setProductsList([...productsRef.current]);
                    setVariantsList([...variantsRef.current]);
                    setLoader(false);
                    return;
                }

                const productId = selectedVariant.productRef;

                // Trova il prodotto associato
                const selectedProduct = productsRef.current.find(p => p.id !== 'total' && p.id === productId);
                if (!selectedProduct) {
                    // Se il prodotto non è trovato, usa i dati completi
                    setProductsList([...productsRef.current]);
                    setVariantsList([...variantsRef.current]);
                    setLoader(false);
                    return;
                }

                // Crea totali specifici
                const variantTotal = {
                    id: 'total',
                    name: 'Total',
                    duration: selectedVariant.duration || 0,
                    visualization: selectedVariant.visualization || 0,
                    sessions: selectedVariant.sessions || 0
                };

                const productTotal = {
                    id: 'total',
                    name: 'Total',
                    duration: selectedProduct.duration || 0,
                    visualization: selectedProduct.visualization || 0,
                    sessions: selectedProduct.sessions || 0
                };

                // Aggiorna le liste visualizzate
                setVariantsList([variantTotal, selectedVariant]);
                setProductsList([productTotal, selectedProduct]);
            }
        } catch (error) {
            // In case of error, use complete data
            setProductsList([...productsRef.current]);
            setVariantsList([...variantsRef.current]);
        } finally {
            setLoader(false);
        }
    }, [glass, variant, dataInitialized]);

    const handleResetFilters = () => {
        dispatch(setAnalyticsFilterOptions({
            ...filterOptions,
            glass: '',
            variant: ''
        }));
    };

    // Renderizza il bottone di reset filters
    const renderResetButton = () => {
        if (glass || variant) {
            return (
                <div className="reset-filter-container">
                    <Button
                        icon={<ReloadOutlined />}
                        onClick={handleResetFilters}
                        type="primary"
                        size="small"
                    >
                        Reset filters
                    </Button>
                </div>
            );
        }
        return null;
    };

    const columns = [
        {
            title: (
                <Row justify="space-between" align="middle" style={{ width: "100%" }}>
                    <Col>
                        <Space size="small">
                            <span>
                                <span>{selectedTab}</span>
                                <span style={{ fontWeight: 300 }}> - Time Focus</span>
                            </span>
                            <CustomTooltip title={tooltipTextMap[selectedTab]}>
                                <InfoCircleOutlined style={{ fontSize: '14px', color: 'inherit' }} />
                            </CustomTooltip>
                        </Space>
                    </Col>
                    <Col>
                        {/* Rimuoviamo il button da qui */}
                    </Col>
                </Row>
            ),
            dataIndex: "name",
            key: "name",
            width: selectedTab === "Variants" ? "35%" : "50%",
            render: (text, row) => {
                // Nelle varianti, sostituisce il nome ma mantiene i colori
                let displayContent = text;

                if (selectedTab === "Variants" && row.id !== 'total' && React.isValidElement(text)) {
                    const productName = productsRef.current.find(p => p.id === row.productRef)?.name || "";
                    const colorIndicators = text.props.children.filter(child =>
                        child && typeof child === 'object' && child.type === ColorIndicator
                    );

                    displayContent = (
                        <Space size="small" align="center">
                            <span>{productName}</span>
                            {colorIndicators}
                        </Space>
                    );
                }

                return (
                    <span
                        style={{
                            cursor: row.id !== 'total' && selectedTab !== "Variants" ? "pointer" : "default",
                            color: row.id !== 'total' ? 'inherit' : '#888'
                        }}
                        onClick={row.id !== 'total' && selectedTab !== "Variants" ? handleSelectionChange(filterOptionMap[selectedTab], row.id) : undefined}
                    >
                        {displayContent}
                    </span>
                );
            },
        },
        // Colonne SKU e Frame Color solo per le varianti
        ...(selectedTab === "Variants" ? [
            {
                title: (
                    <Space size="small">
                        <span>SKU</span>
                        <CustomTooltip title="Stock Keeping Unit - Unique identifier for each product variant">
                            <InfoCircleOutlined style={{ fontSize: '14px', color: 'inherit' }} />
                        </CustomTooltip>
                    </Space>
                ),
                dataIndex: "sku",
                key: "sku",
                width: "15%",
                render: (text, row) => {
                    if (row.id === 'total') return '';
                    return text || '-';
                },
                sorter: (a, b) => {
                    const aValue = a.sku || '';
                    const bValue = b.sku || '';
                    return aValue.localeCompare(bValue);
                },
            },
            {
                title: (
                    <Space size="small">
                        <span>Frame Color</span>
                        <CustomTooltip title="Color of the frame for this variant">
                            <InfoCircleOutlined style={{ fontSize: '14px', color: 'inherit' }} />
                        </CustomTooltip>
                    </Space>
                ),
                dataIndex: "frameColor",
                key: "frameColor",
                width: "15%",
                render: (text, row) => {
                    if (row.id === 'total') return '';
                    return text || '-';
                },
                sorter: (a, b) => {
                    const aValue = a.frameColor || '';
                    const bValue = b.frameColor || '';
                    return aValue.localeCompare(bValue);
                },
            },
        ] : []),
        {
            title: totalRow ? (
                <span style={{ textAlign: 'right', display: 'block' }}>
                    <Space size="small">
                        <span>Visualizations</span>
                        <CustomTooltip title="Number of times this item has been viewed or tried on">
                            <InfoCircleOutlined style={{ fontSize: '14px', color: 'inherit' }} />
                        </CustomTooltip>
                    </Space>
                    <span style={{ fontWeight: 300 }}> - Total </span>
                    <span>{formatNumberBritish(totalRow.visualization, 0)}</span>
                </span>
            ) : (
                <Space size="small">
                    <span>Visualizations</span>
                    <CustomTooltip title="Number of times this item has been viewed or tried on">
                        <InfoCircleOutlined style={{ fontSize: '14px', color: 'inherit' }} />
                    </CustomTooltip>
                </Space>
            ),
            dataIndex: "visualization",
            key: "visualization",
            width: selectedTab === "Variants" ? "17.5%" : "25%",
            align: 'right',
            sorter: (a, b) => a.visualization - b.visualization,
            render: (value) => formatNumberBritish(value, 0),
            defaultSortOrder: 'descend',
        },
        {
            title: totalRow ? (
                <span style={{ textAlign: 'right', display: 'block' }}>
                    <Space size="small">
                        <span>Time</span>
                        <CustomTooltip title="Total time spent viewing or trying on this item">
                            <InfoCircleOutlined style={{ fontSize: '14px', color: 'inherit' }} />
                        </CustomTooltip>
                    </Space>
                    <span style={{ fontWeight: 300 }}> - Total Time Spent </span>
                    <span>{getDurationFromSecond(totalRow.duration || totalRow.time)}</span>
                </span>
            ) : (
                <Space size="small">
                    <span>Time</span>
                    <CustomTooltip title="Total time spent viewing or trying on this item">
                        <InfoCircleOutlined style={{ fontSize: '14px', color: 'inherit' }} />
                    </CustomTooltip>
                </Space>
            ),
            dataIndex: "duration",
            key: "duration",
            width: selectedTab === "Variants" ? "17.5%" : "25%",
            align: 'right',
            render: (text) => getDurationFromSecond(text),
            sorter: (a, b) => a.duration - b.duration,
        },
    ];

    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
    });

    const handleTableChange = (paginationInfo) => {
        setPagination({
            ...pagination,
            current: paginationInfo.current,
            pageSize: paginationInfo.pageSize,
        });
    };

    return (
        <>
            <SummaryGrid>
                {Object.entries(tabMap).map(([key, list]) => {
                    // Calcola il conteggio corretto: elementi diversi da 'total'
                    const filteredList = list.filter(item => item.id !== 'total');
                    const value = filteredList.length;

                    return (
                        <SummaryCard
                            key={key}
                            label={key}
                            value={value}
                            loading={loading}
                            active={key === selectedTab}
                            onClick={() => handleDataIndexChange(key)}
                            toolTipText={tooltipTextMap[key]}
                        />
                    );
                })}
            </SummaryGrid>

            {loading || inloading ? (
                <LoadingCard />
            ) : list.length === 0 ? (
                <Card
                    styles={{ body: { padding: '1rem' } }}
                >
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No data available" />
                </Card>
            ) : (
                <div className="table-container">
                    <Table
                        columns={columns}
                        size="small"
                        dataSource={list}
                        rowKey="id"
                        pagination={{
                            current: pagination.current,
                            pageSize: pagination.pageSize,
                            showSizeChanger: true,
                            pageSizeOptions: [10, 20, 50, 100, 200, 500],
                        }}
                        onChange={handleTableChange}
                        scroll={{
                            x: 1000,
                        }}
                        style={{
                            width: "100%",
                        }}
                        sortDirections={['descend', 'ascend']}
                        defaultSortOrder="descend"
                        rowClassName={(record) => record.id === 'total' ? 'total-row' : ''}
                        showSorterTooltip={false}
                        bordered={false}
                        className="analytics-table"
                    />
                    {renderResetButton()}
                </div>
            )}
            <style jsx="true" global="true">{`
                .table-container {
                    border: 1px solid #d9d9d9;
                    border-radius: 8px;
                    overflow: hidden;
                    position: relative;
                }
                .analytics-table .ant-pagination-item {
                    background: transparent;
                    border: none;
                }
                .analytics-table .ant-pagination-item-active {
                    background: ${primaryShades.primary300};
                }
                .analytics-table .ant-pagination-item-active a {
                    color: ${variantTokens.white};
                }
                .analytics-table .ant-pagination {
                    padding-right: 16px;
                }
                .reset-filter-container {
                    position: absolute;
                    bottom: 16px;
                    left: 16px;
                    z-index: 1;
                }
            `}</style>
        </>
    );
};

export default AnalyticsTable;